import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Check, Copy, Loader2, X } from "lucide-react";
import { toast } from "sonner";

import { fetchSampleBody, type SampleMeta } from "~/lib/feeds";

interface Props {
    slug: string;
    sample: SampleMeta | null;
    open: boolean;
    onOpenChange: (o: boolean) => void;
}

export function SampleViewerDialog({ slug, sample, open, onOpenChange }: Props): React.ReactElement {
    const [body, setBody] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!open || !sample) return;
        let cancel = false;
        setLoading(true);
        setErr(null);
        fetchSampleBody(slug, sample.id)
            .then((t) => {
                if (!cancel) setBody(t);
            })
            .catch((e: Error) => {
                if (!cancel) setErr(e.message);
            })
            .finally(() => {
                if (!cancel) setLoading(false);
            });
        return () => {
            cancel = true;
        };
    }, [open, sample, slug]);

    async function handleCopy(): Promise<void> {
        try {
            await navigator.clipboard.writeText(body);
            setCopied(true);
            toast.success("Copied to clipboard");
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Clipboard copy failed");
        }
    }

    const lines = body.split("\n");
    const preview = lines.slice(0, 500);

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content
                        className="dialog-content enter-up"
                        style={{
                            padding: "var(--space-6)",
                            maxWidth: 960,
                            width: "min(960px, 100vw)",
                            maxHeight: "90vh",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            <Dialog.Title className="type-h2">Sample body</Dialog.Title>
                            <div className="cluster-xs">
                                <button
                                    type="button"
                                    className="btn btn--ghost btn--sm"
                                    onClick={() => void handleCopy()}
                                    disabled={!body}
                                    aria-label="Copy body"
                                >
                                    {copied ? <Check size={14} /> : <Copy size={14} />}
                                    <span>{copied ? "Copied" : "Copy"}</span>
                                </button>
                                <Dialog.Close asChild>
                                    <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                        <X size={14} />
                                    </button>
                                </Dialog.Close>
                            </div>
                        </div>
                        {sample && (
                            <div className="cluster-sm text-12 text-text-muted" style={{ marginTop: "var(--space-2)" }}>
                                <code className="type-mono-sm">{sample.id}</code>
                                <span className="chip">{sample.contentType || "?"}</span>
                                <span className="tabular-nums">{sample.byteCount.toLocaleString()} bytes</span>
                                <span className={`status-pill ${sample.status === "live" ? "status-pill--live" : "status-pill--queued"}`}>
                                    {sample.status}
                                </span>
                            </div>
                        )}

                        <div
                            className="type-mono-sm"
                            style={{
                                marginTop: "var(--space-3)",
                                background: "var(--color-surface-sunken)",
                                border: "1px solid var(--color-border)",
                                borderRadius: "var(--radius-md)",
                                padding: "var(--space-3)",
                                overflow: "auto",
                                flex: 1,
                                fontSize: "var(--font-size-12)",
                                color: "var(--color-text)",
                                minHeight: 200,
                            }}
                        >
                            {loading && (
                                <div className="cluster-xs text-12 text-text-muted">
                                    <Loader2 size={14} className="animate-spin" />
                                    Loading…
                                </div>
                            )}
                            {err && <div className="text-danger text-12">{err}</div>}
                            {!loading && !err && (
                                <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                                    {preview.map((line, i) => (
                                        <div key={i} style={{ display: "flex", gap: 12 }}>
                                            <span className="tabular-nums text-text-subtle" style={{ minWidth: 40, textAlign: "right", userSelect: "none" }}>
                                                {i + 1}
                                            </span>
                                            <span style={{ flex: 1 }}>{line}</span>
                                        </div>
                                    ))}
                                    {lines.length > preview.length && (
                                        <div className="text-12 text-text-muted" style={{ marginTop: 8 }}>
                                            … {(lines.length - preview.length).toLocaleString()} more lines
                                        </div>
                                    )}
                                </pre>
                            )}
                        </div>
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
